import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Logo } from "@/components/Logo";
import { MessageCircle, Mail } from "lucide-react";
import { CONTACT_PHONE, CONTACT_EMAIL, CONTACT_WHATSAPP_LINK } from "@/lib/aps";

export const ADSENSE_CLIENT = "ca-pub-9601131874206913";

export function useAdsense(enabled) {
  useEffect(() => {
    if (!enabled) { document.body.classList.add("no-ads"); return; }
    document.body.classList.remove("no-ads");
    if (!document.getElementById("adsense-script")) {
      const s = document.createElement("script");
      s.id = "adsense-script";
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
      document.head.appendChild(s);
    }
    return () => { document.body.classList.add("no-ads"); };
  }, [enabled]);
}

export function AdSlot({ id }) {
  const slot = process.env.REACT_APP_ADSENSE_SLOT;
  useEffect(() => {
    if (!slot) return;
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) { /* ad blocked */ }
  }, [slot]);
  if (!slot) return null;
  return (
    <div className="my-10" data-testid={`ad-slot-${id}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">Advertisement</p>
      <ins className="adsbygoogle block" style={{ display: "block" }} data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot} data-ad-format="auto" data-full-width-responsive="true" />
    </div>
  );
}

const FOOTER_COLS = [
  { title: "Company", links: [["/about", "About Us"], ["/how-it-works", "How It Works"], ["/services", "Services"], ["/contact", "Contact Us"]] },
  { title: "Learn", links: [["/resources", "Application Guides"], ["/faq", "FAQ"], ["/resources/how-to-calculate-aps-score", "APS Calculator Guide"], ["/resources/how-to-apply-for-nsfas", "NSFAS Guide"]] },
  { title: "Legal", links: [["/terms", "Terms & Conditions"], ["/privacy", "Privacy Policy"], ["/cookie-policy", "Cookie Policy"], ["/disclaimer", "Disclaimer"]] },
];

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2">
          <Logo />
          <p className="text-sm text-slate-500 mt-4 max-w-xs">Helping South African students find the courses they qualify for and apply to universities and TVET colleges — one application at a time.</p>
          <a href={CONTACT_WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B2447] mt-4" data-testid="contact-phone">
            <MessageCircle className="w-4 h-4 text-[#F5C32C]" /> {CONTACT_PHONE} <span className="text-xs text-slate-400">(WhatsApp text only)</span>
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B2447] mt-2 break-all" data-testid="contact-email">
            <Mail className="w-4 h-4 text-[#F5C32C]" /> {CONTACT_EMAIL}
          </a>
        </div>
        {FOOTER_COLS.map((c) => (
          <div key={c.title}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">{c.title}</p>
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              {c.links.map(([to, label]) => (
                <Link key={to} to={to} className="hover:text-[#0B2447]" data-testid={`footer-link-${to.replace(/\//g, "-").slice(1) || "home"}`}>{label}</Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200">
        <p className="max-w-7xl mx-auto px-6 py-5 text-sm text-slate-400">© {new Date().getFullYear()} Your Varsity Application. All rights reserved. We are an independent application-assistance service and are not affiliated with any university, college or NSFAS.</p>
      </div>
    </footer>
  );
}

export function PublicLayout({ eyebrow, title, intro, children, ads = true, wide = false }) {
  useAdsense(ads);
  useEffect(() => { if (title) document.title = `${title} | Your Varsity Application`; }, [title]);
  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <Header />
      <main className={`${wide ? "max-w-6xl" : "max-w-3xl"} w-full mx-auto px-6 py-14 flex-1`}>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{eyebrow}</p>}
        {title && <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0B2447] mt-2 leading-tight" data-testid="page-title">{title}</h1>}
        {intro && <p className="text-slate-600 mt-4 text-base md:text-lg leading-relaxed">{intro}</p>}
        <div className="mt-8">{children}</div>
      </main>
      <SiteFooter />
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl md:text-2xl font-bold text-[#0B2447]">{title}</h2>
      <div className="mt-3 space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}
